import { Box, InputBase, ListItemButton, Popover, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { FAQCourseTag as CourseTag } from "@backend-types";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { getCourseAccessibleLabel } from "./faqCourseA11y";

const POPOVER_MAX_WIDTH = 700;
const POPOVER_WIDTH = 400;

export interface FAQCourseDetails {
  name: string;
  subTitle: string;
}

interface FAQCourseDropdownProps {
  selectedCourse: CourseTag;
  courseDetails: Map<CourseTag, FAQCourseDetails>;
  onSelectCourse: (tag: CourseTag) => void;
  alignRightToRef?: RefObject<HTMLElement | null>;
}

const formatCourseLabel = (details: FAQCourseDetails) =>
  `${details.subTitle}: ${details.name}`;

const isMultiCodeCourse = (tag: CourseTag) => tag.includes("|");

const parseCourseCodes = (tag: CourseTag) => tag.split("|").filter(Boolean);

const courseLabelTypography = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "16px",
  fontStyle: "normal",
  lineHeight: "normal",
} as const;

interface CourseListLabelProps {
  tag: CourseTag;
  details: FAQCourseDetails;
  isSelected: boolean;
}

const CourseListLabel = ({ tag, details, isSelected }: CourseListLabelProps) => {
  if (!isMultiCodeCourse(tag)) {
    return (
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          py: "8px",
          color: isSelected ? "#191919" : "#747474",
          ...courseLabelTypography,
          fontWeight: isSelected ? 700 : 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
        {isSelected ? details.name : formatCourseLabel(details)}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flex: 1,
        minWidth: 0,
        py: "8px",
        gap: "8px",
      }}>
      <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
        {parseCourseCodes(tag).map((code) => (
          <Typography
            key={code}
            component="span"
            sx={{
              color: "#747474",
              ...courseLabelTypography,
              fontWeight: 500,
            }}>
            {code}
          </Typography>
        ))}
      </Box>
      <Box
        sx={{
          width: "1px",
          height: "12px",
          flexShrink: 0,
          backgroundColor: "#D0D0D0",
        }}
      />
      <Typography
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          color: isSelected ? "#191919" : "#747474",
          ...courseLabelTypography,
          fontWeight: isSelected ? 700 : 500,
        }}>
        {details.name}
      </Typography>
    </Box>
  );
};

export const FAQCourseDropdown = ({
  selectedCourse,
  courseDetails,
  onSelectCourse,
  alignRightToRef,
}: FAQCourseDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [courseFilter, setCourseFilter] = useState("");
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number }>();
  const [popoverWidth, setPopoverWidth] = useState(POPOVER_WIDTH);
  const open = Boolean(anchorEl);
  // Snapshot the selected course when opening the dropdown so the list does not re-order
  // while the user is interacting (avoids a brief flicker on selection).
  const [snapshotSelectedCourse, setSnapshotSelectedCourse] = useState<CourseTag | null>(
    null
  );

  const updatePopoverLayout = useCallback(() => {
    if (!anchorEl) return;

    const triggerRect = anchorEl.getBoundingClientRect();
    const top = triggerRect.bottom + 8;

    if (alignRightToRef?.current) {
      const contentRect = alignRightToRef.current.getBoundingClientRect();
      const width = Math.min(POPOVER_WIDTH, POPOVER_MAX_WIDTH);
      setPopoverWidth(width);
      setAnchorPosition({
        top,
        left: contentRect.right - width,
      });
      return;
    }

    setPopoverWidth(POPOVER_WIDTH);
    setAnchorPosition(undefined);
  }, [anchorEl, alignRightToRef]);

  useEffect(() => {
    if (!open) return;

    updatePopoverLayout();
    window.addEventListener("resize", updatePopoverLayout);
    window.addEventListener("scroll", updatePopoverLayout, true);

    return () => {
      window.removeEventListener("resize", updatePopoverLayout);
      window.removeEventListener("scroll", updatePopoverLayout, true);
    };
  }, [open, updatePopoverLayout]);

  const selectedDetails = courseDetails.get(selectedCourse);

  const filteredCourses = useMemo(() => {
    const query = courseFilter.trim().toLowerCase();
    return Array.from(courseDetails.entries()).filter(([tag, details]) => {
      if (!query) return true;
      const haystack = `${tag} ${details.subTitle} ${details.name}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [courseDetails, courseFilter]);

  // Ensure the course selected at the time the dropdown opened stays on top.
  const orderedCourses = useMemo(() => {
    const stableSelectedCourse = snapshotSelectedCourse ?? selectedCourse;

    const selectedIndex = filteredCourses.findIndex(
      ([tag]) => tag === stableSelectedCourse
    );
    if (selectedIndex <= 0) return filteredCourses;

    const selected = filteredCourses[selectedIndex];
    return [selected, ...filteredCourses.filter(([tag]) => tag !== stableSelectedCourse)];
  }, [filteredCourses, snapshotSelectedCourse, selectedCourse]);

  useEffect(() => {
    if (open) {
      setSnapshotSelectedCourse(selectedCourse);
    } else {
      setSnapshotSelectedCourse(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setAnchorEl(null);
    setCourseFilter("");
  };

  const handleSelect = (tag: CourseTag) => {
    // Close first so the user doesn't see the selected item re-ordered while the menu is still open.
    handleClose();
    onSelectCourse(tag);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          if (open) {
            handleClose();
          } else {
            setAnchorEl(e.currentTarget);
          }
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        sx={{
          border: "none",
          background: "none",
          padding: 0,
          cursor: "pointer",
          textAlign: "right",
          color: "#191919",
          fontFamily: '"Proxima Soft", proxima-soft, sans-serif',
          fontSize: "18px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "flex-start",
          gap: "6px",
          "& .courseDropdownTrigger": {
            color: open ? "#191919" : "#747474",
          },
          "&:hover .courseDropdownTrigger": {
            color: open ? "#191919" : "#484848",
          },
        }}>
        FAQ for{" "}
        <Box
          component="span"
          className="courseDropdownTrigger"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}>
            {selectedDetails?.name ?? ""}
            <Box
              sx={{
                mt: "4px",
                height: "2px",
                alignSelf: "stretch",
                borderRadius: "999px",
                background: "currentColor",
              }}
            />
          </Box>
          {open ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 20, color: "inherit" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 20, color: "inherit" }} />
          )}
        </Box>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorPosition ? undefined : anchorEl}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={anchorPosition}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          onMouseDown: (e) => e.stopPropagation(),
          sx: {
            display: "flex",
            width: popoverWidth,
            height: "240px",
            minHeight: "240px",
            maxHeight: "240px",
            maxWidth: `min(${POPOVER_MAX_WIDTH}px, calc(100vw - 48px))`,
            padding: "12px",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "8px",
            borderRadius: "8px",
            border: "2px solid #D0D0D0",
            background: "#FFF",
            boxSizing: "border-box",
            overflow: "hidden",
            boxShadow: "0px 8px 12px 0px rgba(0, 0, 0, 0.19)",
          },
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            flexShrink: 0,
            height: "32px",
            px: "8px",
            borderRadius: "8px",
            border: "2px solid #D0D0D0",
            boxSizing: "border-box",
          }}
          onMouseDown={(e) => e.stopPropagation()}>
          <Box
            component="i"
            className="fa-solid fa-search"
            aria-hidden
            sx={{ color: "#747474", fontSize: "14px", flexShrink: 0 }}
          />
          <InputBase
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
            placeholder="Search courses"
            fullWidth
            sx={{
              fontFamily: '"Proxima Soft", proxima-soft, sans-serif',
              fontSize: "16px",
              fontWeight: 400,
              color: "#191919",
              "& input::placeholder": {
                color: "#747474",
                opacity: 1,
              },
            }}
            inputProps={{ "aria-label": "Search courses" }}
          />
        </Box>
        <Box
          role="listbox"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            width: "100%",
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
          }}>
          {orderedCourses.length === 0 ? (
            <Typography
              sx={{
                px: "16px",
                py: "12px",
                color: "#747474",
                fontSize: "16px",
              }}>
              No courses found
            </Typography>
          ) : (
            orderedCourses.map(([tag, details]) => {
              const isSelected = tag === selectedCourse;
              const isMultiCode = isMultiCodeCourse(tag);
              return (
                <ListItemButton
                  key={tag}
                  role="option"
                  aria-selected={isSelected}
                  aria-label={getCourseAccessibleLabel(
                    tag,
                    details.name,
                    details.subTitle
                  )}
                  onClick={() => handleSelect(tag)}
                  sx={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0 4px",
                    gap: "8px",
                    alignItems: isMultiCode ? "flex-start" : "center",
                    alignSelf: "stretch",
                    flexGrow: 0,
                    flexShrink: 0,
                    borderRadius: isSelected ? "4px" : 0,
                    "&.MuiListItemButton-root": {
                      minHeight: "unset",
                    },
                    border: "1px solid transparent",
                    backgroundColor: isSelected
                      ? "rgba(25, 25, 25, 0.04)"
                      : "transparent",
                    "&:hover": {
                      borderRadius: "4px",
                      backgroundColor: "rgba(25, 25, 25, 0.04)",
                    },
                  }}>
                  <Box
                    sx={{
                      width: 20,
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      ...(isMultiCode ? { alignSelf: "flex-start", mt: "8px" } : {}),
                    }}>
                    {isSelected && (
                      <CheckIcon
                        sx={{
                          color: "#005685",
                          fontFamily: '"Font Awesome 6 Pro", sans-serif',
                          fontSize: "16px",
                          fontStyle: "normal",
                          fontWeight: 900,
                          lineHeight: "normal",
                        }}
                      />
                    )}
                  </Box>
                  <Box aria-hidden sx={{ flex: 1, minWidth: 0 }}>
                    <CourseListLabel
                      tag={tag}
                      details={details}
                      isSelected={isSelected}
                    />
                  </Box>
                </ListItemButton>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};
